/**
 * RecordingRecoveryComponent
 *
 * Mounted at the app root. On load it checks the durable draft store (IndexedDB) for
 * any in-progress AI-Scribe recording that was never uploaded — the signature of a
 * recording interrupted by a HIPAA-timeout logout, reload, or crash (CAPA F131).
 *
 * Because IndexedDB survives the logout path's localStorage/sessionStorage.clear()
 * (utils/httpInterceptor.tsx) and page reloads, the audio is still here. The clinician
 * is offered, per orphaned recording: Recover & submit (reconstruct the blob and POST
 * it to /scribe/jobs) or Discard.
 *
 * It only scans when no recording is currently active, so it never touches the live
 * draft the mini-recorder is actively writing.
 */

import React, { useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  CircularProgress,
} from '@mui/material'
import RestoreIcon from '@mui/icons-material/Restore'
import { toast } from 'react-toastify'

import { RootState } from '../../redux/store'
import { listDrafts, getDraftBlob, deleteDraft, DraftMeta } from '../../utils/recordingPersistence'
import { scribeService, CallType } from '../../services/scribe.service'
import { formatDuration } from '../../hooks/use-audio-recorder'

const CALL_TYPE_DISPLAY: Record<string, string> = {
  CC: 'Cardiology Consultation',
  CF: 'Cardiology Follow-up',
  DI: 'Data Intake',
  ME: 'Medication Evaluation',
  MB: 'Medication Baseline',
  MI: 'Medication Initiation',
  WC: 'Welcome Call',
  OC: 'Onboarding Call',
  ET: 'Early Toxicity',
}

const RecordingRecoveryComponent: React.FC = () => {
  const isRecording = useSelector((s: RootState) => s.recording.isRecording)
  const [drafts, setDrafts] = useState<DraftMeta[]>([])
  const [busyId, setBusyId] = useState<string | null>(null)
  const [scanned, setScanned] = useState(false)

  // Scan once on mount — but only when nothing is actively recording, so we never
  // surface the draft the mini-recorder is currently writing.
  useEffect(() => {
    if (scanned || isRecording) return
    let cancelled = false
    ;(async () => {
      try {
        const found = await listDrafts()
        if (!cancelled) setDrafts(found)
      } catch (_) {
        /* IndexedDB unavailable — nothing to recover */
      } finally {
        if (!cancelled) setScanned(true)
      }
    })()
    return () => { cancelled = true }
  }, [scanned, isRecording])

  const removeFromList = useCallback((id: string) => {
    setDrafts(prev => prev.filter(d => d.id !== id))
  }, [])

  const handleRecover = useCallback(async (draft: DraftMeta) => {
    setBusyId(draft.id)
    try {
      const blob = await getDraftBlob(draft.id)
      if (!blob || blob.size === 0) {
        // Nothing salvageable — drop it so we don't keep offering an empty draft.
        await deleteDraft(draft.id)
        removeFromList(draft.id)
        toast.warning('That recording had no recoverable audio and was discarded.')
        return
      }
      const response = await scribeService.createJob({
        audioFile: blob,
        patientId: draft.patientId,
        callType: draft.callType as CallType,
        gender: draft.gender || undefined,
      })
      if (response.success && response.job_id) {
        await deleteDraft(draft.id)
        removeFromList(draft.id)
        toast.success(
          <span>
            Recovered recording submitted for {draft.patientName}.{' '}
            <a
              href={`/patients/${draft.patientRefId}/details`}
              style={{ color: '#fff', textDecoration: 'underline' }}
            >
              View Results
            </a>
          </span>,
          { autoClose: 10000 }
        )
      } else {
        toast.error(response.error || 'Failed to submit the recovered recording. It has been kept — you can retry.')
      }
    } catch (err: any) {
      // Keep the draft on failure so the clinician can retry on the next load.
      toast.error(err?.message || 'Failed to recover the recording. It has been kept — you can retry.')
    } finally {
      setBusyId(null)
    }
  }, [removeFromList])

  const handleDiscard = useCallback(async (draft: DraftMeta) => {
    setBusyId(draft.id)
    try {
      await deleteDraft(draft.id)
      removeFromList(draft.id)
    } catch (_) {
      /* ignore */
    } finally {
      setBusyId(null)
    }
  }, [removeFromList])

  if (drafts.length === 0) {
    return null
  }

  return (
    <Dialog open maxWidth="sm" fullWidth onClose={() => { /* require an explicit choice */ }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <RestoreIcon color="primary" />
        <Typography variant="h6" component="span">
          Unsaved recording{drafts.length > 1 ? 's' : ''} found
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {drafts.length > 1 ? 'These recordings were' : 'This recording was'} interrupted before
          being saved (for example by a session timeout). The audio was preserved on this device —
          you can submit it for processing or discard it.
        </Typography>
        <Stack spacing={2}>
          {drafts.map(draft => (
            <Box
              key={draft.id}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" noWrap>
                  {draft.patientName}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {CALL_TYPE_DISPLAY[draft.callType] || draft.callType}
                  {draft.durationSeconds > 0 && ` · ${formatDuration(draft.durationSeconds)}`}
                  {` · ${new Date(draft.startedAt).toLocaleString()}`}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleRecover(draft)}
                  disabled={busyId !== null}
                  startIcon={busyId === draft.id ? <CircularProgress size={14} color="inherit" /> : undefined}
                >
                  {busyId === draft.id ? 'Submitting…' : 'Recover & submit'}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => handleDiscard(draft)}
                  disabled={busyId !== null}
                >
                  Discard
                </Button>
              </Stack>
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', ml: 1 }}>
          Choose an action for each recording.
        </Typography>
      </DialogActions>
    </Dialog>
  )
}

export default RecordingRecoveryComponent
